import * as React from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import V2Shell from './components/V2Shell';
import useAuth from '@/src/Authentication/components/hooks/useAuth';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import {
    IconHome,
    IconActivations,
    IconProduction,
} from './components/icons';

/*
 * Org settings chrome (ADMIN-MERGE Phase A) — /v2/org/*. Centered org
 * title, quiet settings nav left, section content right (mock:
 * org-settings.html). ROUTE-gated to Admin/GlobalAdmin — classic hides
 * admin links but leaves the URLs open; v2 doesn't inherit that. The
 * app dock stays, nothing active; the org mark remains the way home.
 */

const ORG_SETTINGS_ROLES = [0, 69]; // Admin, GlobalAdmin

const NavIcon = ({ d }: { d: string }) => (
    <svg
        viewBox="0 0 20 20"
        className="h-[15px] w-[15px] flex-none"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d={d} />
    </svg>
);

/* nav sections appear as they're built (ADMIN-MERGE Phase A order) */
const SECTIONS = [
    {
        key: 'profile',
        label: 'Profile',
        path: '/v2/org',
        icon: 'M10 6.8a3.2 3.2 0 1 1 0 6.4 3.2 3.2 0 0 1 0-6.4zM10 2.8v2M10 15.2v2M2.8 10h2M15.2 10h2M4.9 4.9l1.4 1.4M13.7 13.7l1.4 1.4M15.1 4.9l-1.4 1.4M6.3 13.7l-1.4 1.4',
    },
    {
        key: 'stats',
        label: 'Stats',
        path: '/v2/org/stats',
        icon: 'M4 16V9M10 16V4M16 16v-5',
    },
    {
        key: 'accounts',
        label: 'Accounts',
        path: '/v2/org/accounts',
        icon: 'M10 3.5a3 3 0 1 1 0 6 3 3 0 0 1 0-6zM4 16.5c.8-3 3-4.5 6-4.5s5.2 1.5 6 4.5',
    },
    {
        key: 'whitelabel',
        label: 'Whitelabel',
        path: '/v2/org/whitelabel',
        icon: 'M10 3a7 7 0 1 1 0 14 7 7 0 0 1 0-14zM3 10h14M10 3c2.5 2.3 2.5 11.7 0 14-2.5-2.3-2.5-11.7 0-14z',
    },
    {
        key: 'templates',
        label: 'Email templates',
        path: '/v2/org/templates',
        icon: 'M3.5 5.8h13a.5.5 0 0 1 .5.5v7.4a.5.5 0 0 1-.5.5h-13a.5.5 0 0 1-.5-.5V6.3a.5.5 0 0 1 .5-.5zM3.8 6.5l6.2 4.6 6.2-4.6',
    },
];

export default function V2OrgChrome() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { auth } = useAuth() as any;
    const { org } = useUserOrg();

    if (
        !auth?.roles?.some((role: number) =>
            ORG_SETTINGS_ROLES.includes(role),
        )
    ) {
        return <Navigate to="/v2" replace />;
    }

    return (
        <V2Shell
            outlet={false}
            items={[
                {
                    key: 'home',
                    title: 'Home',
                    icon: <IconHome />,
                    onClick: () => navigate('/v2'),
                },
                {
                    key: 'jobs',
                    title: 'Jobs',
                    icon: <IconProduction />,
                    onClick: () => navigate('/v2/jobs'),
                },
                {
                    key: 'activations',
                    title: 'Activations',
                    icon: <IconActivations />,
                    onClick: () => navigate('/v2/activations'),
                },
            ]}
        >
            <main className="mx-auto max-w-[1020px] px-[clamp(18px,3vw,34px)] pt-4">
                <h1 className="text-center text-[23px] font-bold leading-tight tracking-[-0.02em]">
                    {org?.name ?? 'Organization'}
                </h1>
                <p className="mb-9 mt-1 text-center text-[13px] text-muted-foreground">
                    Organization settings
                </p>

                <div className="grid gap-7 md:grid-cols-[184px_minmax(0,1fr)] md:gap-11">
                    <nav
                        aria-label="Org settings"
                        className="flex gap-1 overflow-x-auto pb-0.5 md:flex-col md:overflow-visible"
                    >
                        {SECTIONS.map((s) => {
                            const active = pathname === s.path;
                            return (
                                <button
                                    key={s.key}
                                    type="button"
                                    onClick={() => navigate(s.path)}
                                    className={
                                        'flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-[9px] border-0 bg-transparent px-3 py-[7px] text-left font-[inherit] text-[13px] font-semibold ' +
                                        (active
                                            ? 'bg-secondary text-foreground'
                                            : 'text-muted-foreground hover:text-foreground')
                                    }
                                >
                                    <NavIcon d={s.icon} />
                                    {s.label}
                                </button>
                            );
                        })}
                        <div className="my-1.5 hidden border-t border-line md:block" />
                        <button
                            type="button"
                            onClick={() => navigate('/v2/devices')}
                            className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-[9px] border-0 bg-transparent px-3 py-[7px] text-left font-[inherit] text-[13px] font-semibold text-muted-foreground hover:text-foreground"
                        >
                            <NavIcon d="M7 3.8h6a1.2 1.2 0 0 1 1.2 1.2v10a1.2 1.2 0 0 1-1.2 1.2H7A1.2 1.2 0 0 1 5.8 15V5A1.2 1.2 0 0 1 7 3.8zM9.2 14.2h1.6" />
                            Devices
                            <span className="ml-auto text-[11px] text-faint">
                                ↗
                            </span>
                        </button>
                    </nav>

                    <div className="min-w-0">
                        <Outlet />
                    </div>
                </div>
            </main>
        </V2Shell>
    );
}
